import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Collapse,
  Paper,
  Switch,
  FormControlLabel,
  Stack,
  Slider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import { apiRequest } from '../utils/api';
import { useTheme } from '@mui/material/styles';

// Classify parsed legs into a readable strategy name — the top-level action
// alone can't tell a short from a long condor.
const classifyAlertStrategy = (parsedData) => {
  const legs = (Array.isArray(parsedData?.legs) ? parsedData.legs : [])
    .filter(l => l && l.strike != null && l.side);
  if (legs.length === 0) return null;
  const isBuy = (l) => String(l.instruction || '').toUpperCase().startsWith('BUY');
  const sideOf = (l) => (String(l.side).toUpperCase().startsWith('P') ? 'P' : 'C');
  const calls = legs.filter(l => sideOf(l) === 'C').sort((a, b) => a.strike - b.strike);
  const puts = legs.filter(l => sideOf(l) === 'P').sort((a, b) => a.strike - b.strike);

  if (legs.length === 1) {
    return `${isBuy(legs[0]) ? 'Long' : 'Short'} ${sideOf(legs[0]) === 'P' ? 'Put' : 'Call'}`;
  }
  if (calls.length === 2 && puts.length === 2) {
    const [lc, hc] = calls;
    const [lp, hp] = puts;
    if (!isBuy(lc) && isBuy(hc) && !isBuy(hp) && isBuy(lp)) return 'Short Iron Condor';
    if (isBuy(lc) && !isBuy(hc) && isBuy(hp) && !isBuy(lp)) return 'Long Iron Condor';
    return 'Iron Condor';
  }
  // lo/hi are sorted ascending by strike. Put credit = buy lower, sell higher.
  // Call credit = sell lower, buy higher.
  const spread = (grp, sideName) => {
    const [lo, hi] = grp;
    const loLong = isBuy(lo), hiLong = isBuy(hi);
    if (sideName === 'Call') {
      if (!loLong && hiLong) return 'Call Credit Spread';
      if (loLong && !hiLong) return 'Call Debit Spread';
      return 'Call Spread';
    }
    if (loLong && !hiLong) return 'Put Credit Spread';
    if (!loLong && hiLong) return 'Put Debit Spread';
    return 'Put Spread';
  };
  if (calls.length === 2 && puts.length === 0) return spread(calls, 'Call');
  if (puts.length === 2 && calls.length === 0) return spread(puts, 'Put');
  if (calls.length === 3 && puts.length === 0) {
    const [a, b, c] = calls;
    if (isBuy(a) && !isBuy(b) && isBuy(c)) return 'Long Call Butterfly';
    if (!isBuy(a) && isBuy(b) && !isBuy(c)) return 'Short Call Butterfly';
    return '3-Leg Call Combo';
  }
  if (puts.length === 3 && calls.length === 0) {
    const [a, b, c] = puts;
    if (isBuy(a) && !isBuy(b) && isBuy(c)) return 'Long Put Butterfly';
    if (!isBuy(a) && isBuy(b) && !isBuy(c)) return 'Short Put Butterfly';
    return '3-Leg Put Combo';
  }
  if (calls.length === 1 && puts.length === 1) {
    const long = isBuy(calls[0]) && isBuy(puts[0]);
    const name = calls[0].strike === puts[0].strike ? 'Straddle' : 'Strangle';
    return `${long ? 'Long' : 'Short'} ${name}`;
  }
  return `${legs.length}-Leg Combo`;
};

const WebhookAlerts = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [includeNonTrade, setIncludeNonTrade] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [tokenFilter, setTokenFilter] = useState('all');
  const [messageFilter, setMessageFilter] = useState('');
  const [daysFilter, setDaysFilter] = useState('7');
  const [fontSize, setFontSize] = useState(() => {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('webhook-alerts-font-size') : null;
    return saved ? parseFloat(saved) : 0.75;
  });

  const PAGE_SIZE = 200;

  const buildUrl = (offset) => {
    const params = new URLSearchParams({ limit: PAGE_SIZE, offset });
    if (daysFilter !== 'all') {
      const since = new Date(Date.now() - Number(daysFilter) * 24 * 60 * 60 * 1000);
      params.set('since', since.toISOString());
    }
    return `/api/webhook/alerts?${params.toString()}`;
  };

  useEffect(() => {
    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysFilter]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest(buildUrl(0));
      if (response.status === 200 && response.data.success) {
        const rows = response.data.data || [];
        setAlerts(rows);
        setHasMore(rows.length === PAGE_SIZE);
      } else {
        setError('Failed to fetch alerts');
      }
    } catch (err) {
      setError('Error fetching alerts');
      console.error('Error fetching alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    try {
      setLoadingMore(true);
      const response = await apiRequest(buildUrl(alerts.length));
      if (response.status === 200 && response.data.success) {
        const rows = response.data.data || [];
        setAlerts(prev => [...prev, ...rows]);
        setHasMore(rows.length === PAGE_SIZE);
      }
    } catch (err) {
      console.error('Error loading more alerts:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    // Format in Eastern Time (ET)
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const getActionColor = (action) => {
    if (!action) return 'default';
    switch (action.toUpperCase()) {
      case 'BOUGHT':
      case 'OPENED':
        return 'success';
      case 'SOLD':
      case 'CLOSED':
        return 'error';
      case 'SHORT':
        return 'warning';
      case 'COVERED':
        return 'info';
      default:
        return 'default';
    }
  };

  const tradeStatusConfig = {
    order_placed: { label: 'Order Placed', color: 'success', icon: '✅' },
    order_failed: { label: 'Order Failed', color: 'error', icon: '❌' },
    autotrading_disabled: { label: 'Auto-Trading Disabled', color: 'default', icon: '⚪' },
    no_parsed_alert: { label: 'No Trade Info', color: 'default', icon: '⚪' },
    mode_mismatch: { label: 'Mode Mismatch', color: 'warning', icon: '⚠️' },
    market_closed: { label: 'Market Closed', color: 'warning', icon: '⏰' },
    missing_fields: { label: 'Missing Fields', color: 'error', icon: '❌' },
    no_account: { label: 'No Account', color: 'error', icon: '❌' },
    no_open_positions: { label: 'No Open Positions', color: 'warning', icon: '⚠️' },
    invalid_expiration: { label: 'Invalid Expiration', color: 'error', icon: '❌' },
    quantity_too_low: { label: 'Quantity Too Low', color: 'warning', icon: '⚠️' },
    manual_review: { label: 'Manual Review', color: 'warning', icon: '✋' },
    order_rejected: { label: 'Order Rejected', color: 'error', icon: '🚫' },
    paper_and_live: { label: 'Paper & Live', color: 'success', icon: '📊' },
    paper_and_live_partial: { label: 'Paper & Live (Partial)', color: 'warning', icon: '⚠️' },
    error: { label: 'Error', color: 'error', icon: '❌' },
  };

  const renderTradeStatus = (tradeStatus) => {
    if (!tradeStatus) return null;
    const config = tradeStatusConfig[tradeStatus.status] || { label: tradeStatus.status, color: 'default', icon: 'ℹ️' };

    const buildOrderEntryUrl = (payload) => {
      if (!payload) return null;
      const params = new URLSearchParams();
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.set(key, value);
        }
      });
      return `/quick-trade?${params.toString()}`;
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <Chip
          label={`${config.icon} ${config.label}`}
          color={config.color}
          size="small"
          variant="contained"
        />
        {tradeStatus.detail && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: `${fontSize}rem` }}>
            {tradeStatus.detail}
          </Typography>
        )}
        {tradeStatus.orderId && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: `${fontSize}rem`, fontFamily: 'monospace' }}>
            ID: {tradeStatus.orderId}
          </Typography>
        )}
        {tradeStatus.manualPayload && (
          <Button
            size="small"
            variant="contained"
            color="warning"
            href={buildOrderEntryUrl(tradeStatus.manualPayload)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ ml: 1, fontSize: `${fontSize}rem` }}
          >
            Load Order Entry
          </Button>
        )}
      </Box>
    );
  };

  const renderParsedData = (parsedData) => {
    if (!parsedData) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Parsed Trade Information
        </Typography>
        <Table size="small">
          <TableBody>
            {parsedData.action && (
              <TableRow>
                <TableCell component="th" scope="row">Action</TableCell>
                <TableCell>
                  <Chip label={parsedData.action} color={getActionColor(parsedData.action)} size="small" />
                </TableCell>
              </TableRow>
            )}
            {classifyAlertStrategy(parsedData) && (
              <TableRow>
                <TableCell component="th" scope="row">Strategy</TableCell>
                <TableCell><strong>{classifyAlertStrategy(parsedData)}</strong></TableCell>
              </TableRow>
            )}
            {parsedData.quantityFraction && (
              <TableRow>
                <TableCell component="th" scope="row">Quantity</TableCell>
                <TableCell>{parsedData.quantityFraction}</TableCell>
              </TableRow>
            )}
            {parsedData.symbol && (
              <TableRow>
                <TableCell component="th" scope="row">Symbol</TableCell>
                <TableCell><strong>{parsedData.symbol}</strong></TableCell>
              </TableRow>
            )}
            {parsedData.dte !== null && (
              <TableRow>
                <TableCell component="th" scope="row">DTE</TableCell>
                <TableCell>{parsedData.dte} days</TableCell>
              </TableRow>
            )}
            {parsedData.expirationDate && (
              <TableRow>
                <TableCell component="th" scope="row">Expiration</TableCell>
                <TableCell>{parsedData.expirationDate}</TableCell>
              </TableRow>
            )}
            {parsedData.strike && (
              <TableRow>
                <TableCell component="th" scope="row">Strike</TableCell>
                <TableCell>${parsedData.strike}</TableCell>
              </TableRow>
            )}
            {parsedData.type && (
              <TableRow>
                <TableCell component="th" scope="row">Type</TableCell>
                <TableCell>{parsedData.type}</TableCell>
              </TableRow>
            )}
            {parsedData.price && (
              <TableRow>
                <TableCell component="th" scope="row">Price</TableCell>
                <TableCell>
                  ${parsedData.price.toFixed(2)}
                  {parsedData.orderType && /CREDIT|DEBIT/.test(String(parsedData.orderType)) && (
                    <Typography component="span" variant="caption" sx={{ ml: 0.5 }}
                      color={String(parsedData.orderType).includes('CREDIT') ? 'success.main' : 'error.main'}>
                      {String(parsedData.orderType).includes('CREDIT') ? 'credit' : 'debit'}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
            {Array.isArray(parsedData.legs) && parsedData.legs.length > 0 && (
              <TableRow>
                <TableCell component="th" scope="row" sx={{ verticalAlign: 'top' }}>Legs</TableCell>
                <TableCell sx={{ p: 0.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ py: 0.25 }}>Instruction</TableCell>
                        <TableCell sx={{ py: 0.25 }}>Side</TableCell>
                        <TableCell sx={{ py: 0.25 }} align="right">Strike</TableCell>
                        <TableCell sx={{ py: 0.25 }}>Expiration</TableCell>
                        <TableCell sx={{ py: 0.25 }} align="right">Qty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedData.legs.map((leg, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ py: 0.25 }}>{leg.instruction || '-'}</TableCell>
                          <TableCell sx={{ py: 0.25 }}>{leg.side || '-'}</TableCell>
                          <TableCell sx={{ py: 0.25 }} align="right">{leg.strike != null ? `$${leg.strike}` : '-'}</TableCell>
                          <TableCell sx={{ py: 0.25 }}>{leg.expiration || '-'}</TableCell>
                          <TableCell sx={{ py: 0.25 }} align="right">{leg.quantity || 1}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableCell>
              </TableRow>
            )}
            {parsedData.parsedByAi && (
              <TableRow>
                <TableCell component="th" scope="row">Parsed By</TableCell>
                <TableCell>
                  <Chip label={`AI (${parsedData.aiSource === 'ai-image' ? 'vision' : 'text'})`} size="small" color="secondary" variant="outlined" />
                </TableCell>
              </TableRow>
            )}
            {parsedData.mentions && parsedData.mentions.length > 0 && (
              <TableRow>
                <TableCell component="th" scope="row">Mentions</TableCell>
                <TableCell>
                  {parsedData.mentions.map((mention, idx) => (
                    <Chip key={idx} label={`@${mention}`} size="small" sx={{ mr: 0.5 }} />
                  ))}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    );
  };

  const NON_TRADE_STATUSES = ['not_a_trade', 'no_parsed_alert'];

  const isTradeAlert = (alert) => {
    const status = alert.tradeStatus?.status;
    if (status && NON_TRADE_STATUSES.includes(status)) return false;
    if (alert.parsedData?.isTradeAlert === true) return true;
    if (alert.rawData?.parsedData?.isTradeAlert === true) return true;
    if (status) return true;
    return false;
  };

  const filteredAlerts = alerts.filter(a => {
    if (!includeNonTrade && !isTradeAlert(a)) return false;
    if (sourceFilter !== 'all' && (a.source || '') !== sourceFilter) return false;
    if (tokenFilter !== 'all' && (a.apiToken?.name || 'Unknown') !== tokenFilter) return false;
    if (messageFilter.trim() && !(a.message || '').toLowerCase().includes(messageFilter.trim().toLowerCase())) return false;
    return true;
  });

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 1, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={fetchAlerts} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          View alerts received via webhook endpoints. Alerts are parsed for trade information when possible.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Message"
            placeholder="Search text…"
            value={messageFilter}
            onChange={(e) => setMessageFilter(e.target.value)}
            sx={{ minWidth: 140, width: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Range</InputLabel>
            <Select value={daysFilter} label="Range" onChange={(e) => setDaysFilter(e.target.value)}>
              <MenuItem value="1">Today</MenuItem>
              <MenuItem value="3">3 days</MenuItem>
              <MenuItem value="7">7 days</MenuItem>
              <MenuItem value="30">30 days</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Source</InputLabel>
            <Select value={sourceFilter} label="Source" onChange={(e) => setSourceFilter(e.target.value)}>
              <MenuItem value="all">All Sources</MenuItem>
              {[...new Set(alerts.map(a => a.source).filter(Boolean))].sort().map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Token</InputLabel>
            <Select value={tokenFilter} label="Token" onChange={(e) => setTokenFilter(e.target.value)}>
              <MenuItem value="all">All Tokens</MenuItem>
              {[...new Set(alerts.map(a => a.apiToken?.name || 'Unknown'))].sort().map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={includeNonTrade}
                onChange={(e) => setIncludeNonTrade(e.target.checked)}
                size="small"
              />
            }
            label="Include non-trade messages"
            labelPlacement="start"
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 220 } }}>
          <Typography variant='caption' sx={{ whiteSpace: 'nowrap' }}>
            Font size
          </Typography>
          <Slider
            value={fontSize}
            onChange={(e, value) => { setFontSize(value); localStorage.setItem('webhook-alerts-font-size', value); }}
            min={0.6}
            max={1.1}
            step={0.05}
            valueLabelDisplay='auto'
            size='small'
            sx={{ flex: 1 }}
          />
        </Box>
        <Typography variant='caption' color='text.secondary'>
          Adjusts alert text size for this page only
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <CardContent>
            <TableContainer>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 36, p: 0.5 }} />
                    <TableCell sx={{ width: 120, p: 0.5 }}>Status</TableCell>
                    <TableCell sx={{ width: 100, p: 0.5 }}>Alert ID</TableCell>
                    <TableCell sx={{ width: 250, p: 0.5 }}>Message</TableCell>
                    <TableCell sx={{ width: 70, p: 0.5 }}>Source</TableCell>
                    <TableCell sx={{ width: 90, p: 0.5 }}>Token</TableCell>
                    <TableCell sx={{ width: 120, p: 0.5 }}>Received</TableCell>
                    <TableCell sx={{ width: 90, p: 0.5 }}>IP Address</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAlerts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          {includeNonTrade ? 'No webhook alerts received yet.' : 'No trade alerts found. Toggle "Include non-trade messages" to see all alerts.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAlerts.map((alert) => (
                      <React.Fragment key={alert.id}>
                        <TableRow>
                          <TableCell sx={{ p: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => toggleRow(alert.id)}
                              sx={{ p: 0.25 }}
                            >
                              {expandedRows[alert.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ p: 0.5, verticalAlign: 'top' }}>
                            {renderTradeStatus(alert.tradeStatus)}
                          </TableCell>
                          <TableCell sx={{ p: 0.5, verticalAlign: 'top' }}>
                            <Typography variant="caption" fontFamily="monospace" sx={{ fontSize: `${fontSize}rem`, wordBreak: 'break-all' }}>
                              {alert.alertId}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: 0.5, verticalAlign: 'top' }}>
                            <Typography variant="caption" sx={{ wordBreak: 'break-word', whiteSpace: 'normal', fontSize: `${fontSize}rem`, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {alert.message || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: 0.5, verticalAlign: 'top' }}>
                            <Chip label={alert.source} size="small" variant="contained" />
                          </TableCell>
                          <TableCell sx={{ p: 0.5, verticalAlign: 'top' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: `${fontSize}rem`, wordBreak: 'break-word' }}>
                              {alert.apiToken?.name || 'Unknown'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: 0.5, verticalAlign: 'top' }}>
                            <Typography variant="caption" sx={{ fontSize: `${fontSize}rem`, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {formatDateTime(alert.receivedAt)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: 0.5, verticalAlign: 'top' }}>
                            <Typography variant="caption" fontFamily="monospace" sx={{ fontSize: `${fontSize}rem`, wordBreak: 'break-all' }}>
                              {alert.ipAddress || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                            <Collapse in={expandedRows[alert.id]}>
                              <Box sx={{ p: 2, bgcolor: isDark ? 'grey.800' : 'grey.50' }}>
                                {renderParsedData(alert.parsedData)}
                                
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Raw Data
                                  </Typography>
                                  <Paper sx={{ p: 1, bgcolor: isDark ? 'grey.900' : 'grey.100' }}>
                                    <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: `${fontSize}rem`, overflow: 'auto' }}>
                                      {JSON.stringify(alert.rawData, null, 2)}
                                    </Typography>
                                  </Paper>
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {!loading && hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : `Load older alerts`}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default WebhookAlerts;
